// src/components/TicketsMobile.jsx
import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Skeleton,
  Button,
  FormControl,
  Select,
  CssBaseline,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import DeleteTicketModal from "../forms/DeleteTicketModal";
import UploadFilesModal from "../uploaders/UploadFilesModal";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  collectionGroup,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  runTransaction,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { db, storage } from "../firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { showCustomToast } from "../misc/CustomToast";
import EditTicketModalMobile from "../forms/EditTicketModalMobile";
import TicketLineAdminMobile from "./Lines/TicketLineAdminMobile";
import TicketLineMobile from "./Lines/TicketLineMobile";
import AddProjectModalMobile from "./AddProjectModalMobile";
import { LanguageContext } from "../context/LanguageContext";
import AddTicketModalMobile from "./AddTicketModalMobile";
import MobileHeader from "./headers/MobileHearder";
import SearchIcon from "@mui/icons-material/Search";
import { useIsPremium } from "../hooks/useIsPremium";
import { useMonthlyTicketUsage } from "../hooks/useMonthlyTicketUsage";

export default function TicketsMobile() {
  const { translate } = useContext(LanguageContext);
  const { currentUser, logOut } = useAuth();
  const navigate = useNavigate();
  const { projectId: urlProjectId, ticketId: urlTicketId } = useParams();
  const [notifications, setNotifications] = useState([]);
  const [anchorNotif, setAnchorNotif] = useState(null);
  const [anchorProfile, setAnchorProfile] = useState(null);
  const handleBellClose = () => setAnchorNotif(null);
  const handleProfileClose = () => setAnchorProfile(null);
  const [openAddProjectModal, setOpenAddProjectModal] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [role, setRole] = useState("Normal User");
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [accessibleProjects, setAccessibleProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(urlProjectId || null);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [openAddTicketModal, setOpenAddTicketModal] = useState(false);
  const [ticketToEdit, setTicketToEdit] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [, setLoading] = useState(false);
  const { isPremium } = useIsPremium();
  const [q, setQ] = useState("");
  const { monthlyCount, monthlyLimit } = useMonthlyTicketUsage(selectedProject);
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: darkMode ? "#121212" : "#f4f8f9",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
          ...(darkMode && { text: { primary: "#fff", secondary: "#ccc" } }),
        },
      }),
    [darkMode]
  );

  // **Constantes d’onglets**
  const statusTabs = [
    { key: "All", labelKey: "All" },
    { key: "Open", labelKey: "Open" },
    { key: "Working on It", labelKey: "WorkingOnIt" },
    { key: "In Staging", labelKey: "InStaging" },
    { key: "In Test", labelKey: "InTest" },
    { key: "In Review", labelKey: "InReview" },
    { key: "In Production", labelKey: "InProduction" },
    { key: "Closed", labelKey: "Closed" },
  ];

  useEffect(() => {
    if (!currentUserProfile?.companyId) return;
    const q = query(
      collection(db, "profilsUsers"),
      where("companyId", "==", currentUserProfile.companyId)
    );
    const unsub = onSnapshot(q, (snap) =>
      setCompanyUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [currentUserProfile]);

  // — Charger profil & notifications
  useEffect(() => {
    if (!currentUser) return;
    const profRef = doc(db, "profilsUsers", currentUser.uid);
    const unsubProf = onSnapshot(profRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role);
      setCurrentUserProfile({ id: snap.id, ...data });
      if (typeof data.darkMode === "boolean") {
        setDarkMode(data.darkMode);
      }
    });
    const notifCol = collection(
      db,
      "profilsUsers",
      currentUser.uid,
      "notifications"
    );
    const unsubNotif = onSnapshot(notifCol, (snap) =>
      setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => {
      unsubProf();
      unsubNotif();
    };
  }, [currentUser]);
  // — Charger projets
  useEffect(() => {
    if (!currentUserProfile?.companyId) return;
    const q = query(
      collection(db, "projects"),
      where("companyId", "==", currentUserProfile.companyId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const projs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllProjects(projs);
    });
    return unsub;
  }, [currentUserProfile]);

  useEffect(() => {
    const handler = (e) => setDarkMode(Boolean(e.detail));
    window.addEventListener("darkmode:changed", handler);
    return () => window.removeEventListener("darkmode:changed", handler);
  }, []);

  useEffect(() => {
    if (role === "Admin") {
      setAccessibleProjects(allProjects);
    } else {
      setAccessibleProjects(
        allProjects.filter(
          (p) =>
            Array.isArray(p.memberIds) && p.memberIds.includes(currentUser.uid)
        )
      );
    }
  }, [allProjects, role, currentUser]);

  useEffect(() => {
    if (urlProjectId && accessibleProjects.find((p) => p.id === urlProjectId)) {
      setSelectedProject(urlProjectId);
    }
  }, [urlProjectId, accessibleProjects]);

  useEffect(() => {
    if (!currentUserProfile?.companyId || !selectedProject) {
      setTickets([]);
      setTicketsLoading(false);
      return;
    }
    setTicketsLoading(true);
    const ticketsRef =
      selectedProject === "__all"
        ? collectionGroup(db, "tickets")
        : collection(db, "projects", selectedProject, "tickets");
    const constraints =
      selectedProject === "__all"
        ? [where("companyId", "==", currentUserProfile.companyId)]
        : [];
    const q = query(ticketsRef, ...constraints);
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
      setTickets(docs);
      setTicketsLoading(false);
    });
    return unsub;
  }, [currentUserProfile, selectedProject, role]);

  const handleCreateProjectMobile = async (name, members) => {
    if (!isPremium && allProjects.length >= 1) {
      showCustomToast(translate("PremiumNeededForMoreProjects"), "error");
      return;
    }
    if (!name.trim() || !currentUserProfile) return;
    const id = name.trim();
    const projRef = doc(db, "projects", id);
    const snap = await getDoc(projRef);
    if (snap.exists()) {
      showCustomToast(translate("ProjectAlreadyExists"), "error");
      return;
    }
    const proj = {
      name: id,
      companyId: currentUserProfile.companyId,
      companyName: currentUserProfile.companyName,
      pageListCategories: [], // ou tes catégories par défaut
      createdAt: new Date(),
      memberIds: [currentUser.uid, ...members.map((u) => u.id)],
      memberEmails: [currentUser.email, ...members.map((u) => u.email)],
    };
    await setDoc(projRef, proj);
    await setDoc(doc(db, "projects", id, "meta", "counter"), {
      nextTicketNumber: 0,
    });
    showCustomToast(translate("ProjectCreated"), "success");
    setOpenAddProjectModal(false);
    setSelectedProject(id);
  };

  // — Statistiques par status
  const statusCounts = useMemo(() => {
    const c = { All: tickets.length };
    statusTabs.forEach(({ key }) => {
      if (key !== "All")
        c[key] = tickets.filter((t) => t.status === key).length;
    });
    return c;
  }, [tickets]);

  const displayedTickets = useMemo(() => {
    const { key } = statusTabs[tabIndex];
    let base =
      key === "All" ? tickets : tickets.filter((t) => t.status === key);
    if (isPremium && q.trim()) {
      const s = q.trim().toLowerCase();
      base = base.filter(
        (t) =>
          (t.ticketName || "").toLowerCase().includes(s) ||
          (t.originalQuestion || "").toLowerCase().includes(s) ||
          (t.ticketNumber || "").toLowerCase().includes(s)
      );
    }
    return base;
  }, [tickets, tabIndex, isPremium, q]);

  // **Handlers simples**
  const handleProjectChange = (id) => {
    if (id === "__add") {
      // ouvre un modal de création de projet (à implémenter)
      setOpenAddProjectModal(true);
      return;
    }
    setSelectedProject(id);
    navigate(`/app/projects/${encodeURIComponent(id)}`);
  };
  const handleOpenAddTicket = () => setOpenAddTicketModal(true);
  const handleCloseAddTicket = () => setOpenAddTicketModal(false);

  const handleOpenEdit = (ticket) => {
    setTicketToEdit(ticket);
    navigate(`/app/projects/${selectedProject}/tickets/${ticket.id}`);
  };
  const handleCloseEdit = () => {
    setTicketToEdit(null);
    navigate(`/app/projects/${selectedProject}`);
  };

  const handleDelete = (id) => {
    setTicketToDelete(id);
    setDeleteModalOpen(true);
  };
  const handleCloseDelete = () => setDeleteModalOpen(false);

  const handleSubmitTicket = async ({
    ticketName,
    originalQuestion,
    ticketType,
    pageList,
    priority,
    attachments,
  }) => {
    if (!selectedProject) {
      showCustomToast(translate("SelectProject"), "error");
      return;
    }
    if (!isPremium && monthlyCount >= monthlyLimit) {
      showCustomToast(translate("MonthlyTicketCapReached"), "error");
      return;
    }
    setLoading(true);
    try {
      // 1️⃣ Récupérer et incrémenter le compteur atomiquement
      const counterRef = doc(db, "meta", "ticketCounter");
      let generatedNumber;
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const next = (snap.exists() ? snap.data().nextTicketNumber : 0) + 1;
        tx.set(counterRef, { nextTicketNumber: next }, { merge: true });
        const prefix = selectedProject.toUpperCase().slice(0, 4);
        generatedNumber = `${prefix}-${String(next).padStart(6, "0")}`;
      });

      // 2️⃣ Créer le ticket
      const newTicket = {
        ticketName,
        ticketType,
        originalQuestion,
        pageList,
        priority,
        createdBy: currentUser.displayName || "Anonymous",
        createdById: currentUser.uid,
        createdAt: new Date(),
        status: "Open",
        ticketNumber: generatedNumber,
        companyId: currentUserProfile.companyId,
        companyName: currentUserProfile.companyName,
        projectId: selectedProject,
      };
      const ticketRef = await addDoc(
        collection(db, "projects", selectedProject, "tickets"),
        newTicket
      );

      // 3️⃣ Upload des pièces jointes
      if (attachments.length) {
        const urls = [];
        for (let { file } of attachments) {
          const path = `tickets/${ticketRef.id}/${file.name}`;
          const snap = await uploadBytes(storageRef(storage, path), file);
          urls.push(await getDownloadURL(snap.ref));
        }
        await updateDoc(ticketRef, { images: urls });
      }

      // 4️⃣ Ajouter une entrée d’activité “Ticket créé”
      await addDoc(
        collection(
          db,
          "projects",
          selectedProject,
          "tickets",
          ticketRef.id,
          "activity"
        ),
        {
          action: "Ticket créé",
          performedBy: currentUser.displayName,
          timestamp: new Date(),
        }
      );

      showCustomToast(translate("TicketAddedSuccessfully"), "success");
      setOpenAddTicketModal(false);
    } catch (error) {
      console.error("Error adding ticket:", error);
      showCustomToast(translate("FailedToAddTicket"), "error");
    } finally {
      setLoading(false);
    }
  };

  // 2) Mise à jour d’un ticket existant
  const handleUpdateTicket = async (updatedTicket) => {
    if (!ticketToEdit) return;
    setEditLoading(true);
    try {
      const ticketDocRef = doc(
        db,
        "projects",
        selectedProject,
        "tickets",
        ticketToEdit.id
      );

      // — Gestion des pièces jointes
      const oldUrls = ticketToEdit.images || [];
      const keptUrls = updatedTicket.images
        .filter((a) => a.url)
        .map((a) => a.url);
      // suppression des anciennes pièces non conservées
      for (let url of oldUrls.filter((u) => !keptUrls.includes(u))) {
        await deleteObject(storageRef(storage, url)).catch(() => {});
      }
      // upload des nouvelles
      const newUrls = [];
      for (let { file } of updatedTicket.images.filter((a) => a.file)) {
        const path = `tickets/${ticketToEdit.id}/${file.name}`;
        const snap = await uploadBytes(storageRef(storage, path), file);
        newUrls.push(await getDownloadURL(snap.ref));
      }
      const imagesField = [...keptUrls, ...newUrls];

      // — Collecte des changements pour l’historique
      const changes = [];
      if (
        updatedTicket.status &&
        updatedTicket.status !== ticketToEdit.status
      ) {
        changes.push(
          `Statut → ${ticketToEdit.status} → ${updatedTicket.status}`
        );
      }
      if (
        updatedTicket.priority &&
        updatedTicket.priority !== ticketToEdit.priority
      ) {
        changes.push(
          `Priorité → ${ticketToEdit.priority} → ${updatedTicket.priority}`
        );
      }
      if (
        updatedTicket.originalQuestion &&
        updatedTicket.originalQuestion !== ticketToEdit.originalQuestion
      ) {
        changes.push("Description modifiée");
      }
      if (
        updatedTicket.ticketType &&
        updatedTicket.ticketType !== ticketToEdit.ticketType
      ) {
        changes.push(
          `Type → ${ticketToEdit.ticketType} → ${updatedTicket.ticketType}`
        );
      }

      await updateDoc(ticketDocRef, {
        ...(!changes.length && { images: imagesField }), // si aucun autre changement
        ...(changes.length && {
          status: updatedTicket.status,
          priority: updatedTicket.priority,
          originalQuestion: updatedTicket.originalQuestion,
          ticketType: updatedTicket.ticketType,

          images: imagesField,
        }),
      });

      for (let action of changes) {
        await addDoc(
          collection(
            db,
            "projects",
            selectedProject,
            "tickets",
            ticketToEdit.id,
            "activity"
          ),
          {
            action,
            performedBy: currentUser.displayName,
            timestamp: new Date(),
          }
        );
      }

      showCustomToast(translate("TicketUpdatedSuccessfully"), "success");
      handleCloseEdit();
    } catch (error) {
      console.error("Error updating ticket:", error);
      showCustomToast(translate("FailedToUpdateTicket"), "error");
    } finally {
      setEditLoading(false);
    }
  };

  const handlePerformDelete = async () => {
    if (!ticketToDelete) return;
    try {
      // supprime le doc
      await deleteDoc(
        doc(db, "projects", selectedProject, "tickets", ticketToDelete)
      );
      showCustomToast(translate("TicketDeleted"), "success");
    } catch (error) {
      console.error("Error deleting ticket:", error);
      showCustomToast(translate("FailedToDeleteTicket"), "error");
    } finally {
      setDeleteModalOpen(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
        }}
      >
      <MobileHeader
        title={translate("SupportTickets")}
        showProfileItem={false}
      />

      <Menu
        anchorEl={anchorNotif}
        open={!!anchorNotif}
        onClose={handleBellClose}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            maxHeight: "50vh",
            width: 280,
          },
        }}
      >
        {notifications.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary={translate("NoNotifications")} />
          </MenuItem>
        ) : (
          notifications.map((n) => (
            <MenuItem
              key={n.id}
              onClick={async () => {
                await updateDoc(
                  doc(
                    db,
                    "profilsUsers",
                    currentUser.uid,
                    "notifications",
                    n.id
                  ),
                  { read: true }
                );
                handleBellClose();
              }}
            >
              <ListItemText
                primary={n.message}
                // secondary={new Date(
                //   n.timestamp.seconds * 1000
                // ).toLocaleString()}
                secondary={
                  n?.timestamp?.seconds
                    ? new Date(n.timestamp.seconds * 1000).toLocaleString()
                    : ""
                }
              />
            </MenuItem>
          ))
        )}
      </Menu>

      <Menu
        anchorEl={anchorProfile}
        open={!!anchorProfile}
        onClose={handleProfileClose}
        PaperProps={{
          sx: { bgcolor: "background.paper", color: "text.primary" },
        }}
      >
        <MenuItem
          onClick={() => {
            handleProfileClose();
            navigate("/app/gotopremium");
          }}
        >
          <ListItemText primary={translate("GoPremium")} />
        </MenuItem>
        <MenuItem
          onClick={async () => {
            handleProfileClose();
            await logOut();
            navigate("/login");
          }}
        >
          <ListItemText primary={translate("Logout")} />
        </MenuItem>
      </Menu>

      <Box sx={{ px: 2, pt: 1 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", mb: 1, width: "100%" }}
        >
          <Box sx={{ flexGrow: 1, width: "50%" }}>
            <FormControl fullWidth size="small">
              <Select
                value={selectedProject ?? ""}
                onChange={(e) => handleProjectChange(e.target.value)}
                displayEmpty
                renderValue={(val) =>
                  val
                    ? accessibleProjects.find((p) => p.id === val)?.name ??
                      (val === "__all" ? translate("All") : val)
                    : translate("ChooseAProject")
                }
                sx={{
                  bgcolor: "background.paper",
                  color: "text.primary",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { bgcolor: "background.paper", color: "text.primary" },
                  },
                }}
              >
                {/* ⚠️ Pas d'item vide ici → le placeholder n'apparaît que quand c'est fermé */}
                {isPremium && (
                  <MenuItem value="__all">{translate("All")}</MenuItem>
                )}
                {accessibleProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
                <MenuItem value="__add" sx={{ fontStyle: "italic" }}>
                  {translate("AddAProject")}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <div
            style={{ width: "50%", display: "flex", justifyContent: "center" }}
          >
            <IconButton color="primary" onClick={handleOpenAddTicket}>
              <Button variant="contained">{translate("AddNewTicket")} </Button>
            </IconButton>
          </div>
        </Box>

        {isPremium && (
          <TextField
            fullWidth
            size="small"
            placeholder={translate("SearchTicketsPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.paper",
                "& fieldset": { borderColor: "divider" },
                "&:hover fieldset": { borderColor: "divider" },
                "&.Mui-focused fieldset": { borderColor: "primary.main" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}

        <Tabs
          value={tabIndex}
          onChange={(_, i) => setTabIndex(i)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ bgcolor: "background.paper", borderRadius: 1, mb: 1 }}
        >
          {statusTabs.map(({ key, labelKey }) => (
            <Tab
              key={key}
              label={`${translate(labelKey)} (${statusCounts[key] || 0})`}
            />
          ))}
        </Tabs>

        {ticketsLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={60} sx={{ mb: 1 }} />
          ))
        ) : displayedTickets.length === 0 ? (
          <Typography align="center" sx={{ mt: 4 }}>
            {translate("NoTicketsToShow")}
          </Typography>
        ) : (
          displayedTickets.map((t) => {
            const canEdit =
              t.createdById === currentUser.uid || role === "Admin";
            return role === "Admin" ? (
              <TicketLineAdminMobile
                key={t.id}
                ticket={t}
                onEdit={() => handleOpenEdit(t)}
                onDelete={() => handleDelete(t.id)}
                creator={{ name: t.createdBy, photoUrl: t.creatorPhoto }}
              />
            ) : (
              <TicketLineMobile
                key={t.id}
                ticket={t}
                canEdit={true} // <— on le force cliquable
                onEdit={() => handleOpenEdit(t)}
                canDelete={canEdit} // optionnel, si ton Line gère delete via cette prop
                onDelete={canEdit ? () => handleDelete(t.id) : undefined}
                creator={{ name: t.createdBy, photoUrl: t.creatorPhoto }}
              />
            );
          })
        )}
      </Box>

      <AddTicketModalMobile
        open={openAddTicketModal}
        onClose={handleCloseAddTicket}
        accessibleProjects={accessibleProjects}
        selectedProject={selectedProject}
        handleProjectChange={handleProjectChange}
        handleSubmitTicket={handleSubmitTicket}
        loading={false}
      />
      <AddProjectModalMobile
        open={openAddProjectModal}
        onClose={() => setOpenAddProjectModal(false)}
        companyUsers={companyUsers.filter((u) => u.id !== currentUser.uid)}
        handleCreateProject={handleCreateProjectMobile}
      />

      <EditTicketModalMobile
        open={!!ticketToEdit}
        onClose={handleCloseEdit}
        ticket={ticketToEdit}
        handleUpdate={handleUpdateTicket}
        loading={editLoading}
        role={role}
        selectedProject={selectedProject}
      />
      <DeleteTicketModal
        open={deleteModalOpen}
        onClose={handleCloseDelete}
        ticketId={ticketToDelete}
        selectedProject={selectedProject}
      />
        <UploadFilesModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          files={ticketToEdit?.images || []}
          setFiles={() => {}}
        />
      </Box>
    </ThemeProvider>
  );
}
